import React from 'react';
import styled from 'styled-components';
import api from '../api/axiosConfig';
import SEO from '../components/SEO';
import { useTranslation } from 'react-i18next';

const DEFAULT_AVATAR =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64">
    <rect width="64" height="64" rx="16" fill="#053969"/>
    <circle cx="32" cy="26" r="12" fill="#b7ff53"/>
    <path d="M12 58c4-12 14-18 20-18s16 6 20 18" fill="#b7ff53"/>
  </svg>
`);

const Page = styled.div`
  width: min(100%, 1800px);
  margin: 18px auto;
  padding: 18px;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 16px 46px rgba(0, 0, 0, 0.18);
`;

const Title = styled.h1`
  margin: 0;
  font-weight: 950;
  color: #053969;
`;

const Card = styled.div`
  border-radius: 18px;
  padding: 14px;
  background: rgba(5, 57, 105, 0.04);
  border: 1px solid rgba(5, 57, 105, 0.12);
`;

const Row = styled.div`
  display: grid;
  gap: 12px;
`;

const Input = styled.input`
  border: 1px solid rgba(5, 57, 105, 0.18);
  background: rgba(255, 255, 255, 0.85);
  border-radius: 14px;
  padding: 12px 12px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: rgba(5, 57, 105, 0.45);
    box-shadow: 0 0 0 4px rgba(5, 57, 105, 0.12);
  }
`;

const Textarea = styled.textarea`
  border: 1px solid rgba(5, 57, 105, 0.18);
  background: rgba(255, 255, 255, 0.85);
  border-radius: 14px;
  padding: 12px 12px;
  min-height: 110px;
  font-size: 14px;
  outline: none;
  resize: vertical;

  &:focus {
    border-color: rgba(5, 57, 105, 0.45);
    box-shadow: 0 0 0 4px rgba(5, 57, 105, 0.12);
  }
`;

const Btn = styled.button`
  border: 1px solid rgba(0, 0, 0, 0.16);
  background: #fff;
  border-radius: 999px;
  padding: 10px 12px;
  cursor: pointer;
  font-weight: 900;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ReviewItem = styled.div`
  display: grid;
  grid-template-columns: 48px 1fr;
  gap: 12px;
  align-items: start;
`;

const Avatar = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.12);
`;

const Meta = styled.div`
  display: flex;
  gap: 10px;
  align-items: baseline;
  flex-wrap: wrap;
`;

const Author = styled.div`
  font-weight: 950;
`;

const DateText = styled.div`
  font-weight: 800;
  opacity: 0.6;
  font-size: 12px;
`;

const Text = styled.div`
  margin-top: 6px;
  font-weight: 800;
  opacity: 0.9;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const Info = styled.div`
  margin-top: 10px;
  opacity: 0.75;
  font-weight: 800;
`;

/* ✅ safety: avoid "/api/api/..." */
function safeApiPath(path) {
  const p = String(path || '');
  if (!p) return p;

  const normalized = p.replace(/\/{2,}/g, '/');

  if (normalized.startsWith('/api/')) return normalized.replace(/^\/api/, '');
  if (normalized.startsWith('api/')) return normalized.replace(/^api/, '');

  if (normalized === '/api') return '/';
  if (normalized === 'api') return '/';

  return normalized;
}

export default function Reviews() {
  const { t } = useTranslation();

  const [items, setItems] = React.useState([]); // всегда массив
  const [loading, setLoading] = React.useState(true);

  const [name, setName] = React.useState('');
  const [text, setText] = React.useState('');
  const [sending, setSending] = React.useState(false);

  const isAuthed = !!localStorage.getItem('access');

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(safeApiPath('/reviews/'));
      setItems(res.data);
    } catch (e) {
      console.error(e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const send = async (e) => {
    e.preventDefault();

    if (!isAuthed) {
      alert(t('reviews.alerts.signInFirst'));
      return;
    }

    if (text.trim().length < 3) {
      alert(t('reviews.alerts.tooShort'));
      return;
    }

    try {
      setSending(true);

      await api.post(safeApiPath('/reviews/create/'), {
        author_name: name.trim(),
        text: text.trim(),
      });

      setName('');
      setText('');
      await load();
      alert(t('reviews.alerts.posted'));
    } catch (e) {
      console.error(e);
      alert(t('reviews.alerts.failed'));
    } finally {
      setSending(false);
    }
  };

  const list = Array.isArray(items) ? items : [];

  return (
    <Page>
      <SEO
        title="Reviews"
        description="User reviews for website templates and services."
        path="/reviews"
      />

      <Title>{t('reviews.title')}</Title>

      <Card style={{ marginTop: 12 }}>
        <form onSubmit={send}>
          <Row>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('reviews.form.namePlaceholder')}
            />
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t('reviews.form.textPlaceholder')}
            />
            <Btn type="submit" disabled={sending}>
              {sending ? t('reviews.form.posting') : t('reviews.form.post')}
            </Btn>

            {!isAuthed ? <Info>{t('reviews.form.mustSignIn')}</Info> : null}
          </Row>
        </form>
      </Card>

      <div style={{ marginTop: 14, display: 'grid', gap: 10 }}>
        {loading ? (
          <div>{t('reviews.loading')}</div>
        ) : list.length === 0 ? (
          <div style={{ opacity: 0.75 }}>{t('reviews.empty')}</div>
        ) : (
          list.map((r) => (
            <Card key={r.id || `${r.author_name}-${r.created_at}-${r.text?.slice?.(0, 12)}`}>
              <ReviewItem>
                <Avatar src={DEFAULT_AVATAR} alt="" />
                <div>
                  <Meta>
                    <Author>{r.author}</Author>
                    <DateText>
                      {r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}
                    </DateText>
                  </Meta>
                  <Text>{r.text || ''}</Text>
                </div>
              </ReviewItem>
            </Card>
          ))
        )}
      </div>
    </Page>
  );
}
